import type { WorkflowSelectedValues } from "./choiceModel";
import { readProcessingEvents } from "./processingStream";

import { getHeaders } from "../../api/api";
import { DocumentationApiError, DocumentationDraft, DocumentationGroupResponse, DocumentationProgressEvent, DocumentationWorkflowNodeChanges } from "./models";

const BACKEND_URI = ((import.meta as any)?.env?.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

async function headers(token: string | undefined, idempotencyKey?: string): Promise<Record<string, string>> {
    return {
        ...(await getHeaders(token)),
        "Content-Type": "application/json",
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {})
    };
}

async function checked(response: Response): Promise<Response> {
    if (response.ok) return response;
    let code = "DOCUMENTATION_REQUEST_FAILED";
    let message = `Documentation request failed (${response.status}).`;
    let details: Record<string, unknown> | undefined;
    try {
        const payload = await response.json();
        code = payload?.error?.code || code;
        message = payload?.error?.message || message;
        details = payload?.error?.details;
    } catch {
        // Preserve the status-based error when the server did not return JSON.
    }
    throw new DocumentationApiError(code, message, response.status, details);
}

export async function createDocumentationGroup(
    projectIds: string[],
    rawText: string,
    language: string,
    token: string | undefined,
    idempotencyKey: string,
    signal?: AbortSignal,
    routedWorkflowId?: string
): Promise<DocumentationGroupResponse> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/documentation-groups`, {
            method: "POST",
            headers: await headers(token, idempotencyKey),
            body: JSON.stringify({
                project_ids: projectIds,
                language,
                ...(routedWorkflowId ? { routed_workflow_id: routedWorkflowId } : {}),
                input: { source: "text", raw_text: rawText }
            }),
            signal
        })
    );
    return response.json();
}

export async function summarizeDocumentationChanges(
    projectId: string,
    changes: Record<string, unknown>,
    language: string,
    token: string | undefined
): Promise<string> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(projectId)}/documentation-change-summary`, {
            method: "POST",
            headers: await headers(token),
            body: JSON.stringify({ changes, language })
        })
    );
    const result: { summary: string } = await response.json();
    return result.summary;
}

export async function getDocumentationGroup(
    groupId: string,
    token: string | undefined,
    signal?: AbortSignal
): Promise<DocumentationGroupResponse> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/documentation-groups/${encodeURIComponent(groupId)}`, {
            headers: await getHeaders(token),
            signal
        })
    );
    return response.json();
}

export async function getDocumentationDraft(projectId: string, draftId: string, token: string | undefined): Promise<DocumentationDraft> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(projectId)}/documentation-drafts/${encodeURIComponent(draftId)}`, {
            headers: await getHeaders(token)
        })
    );
    return response.json();
}

export async function submitDocumentationCard(draft: DocumentationDraft, instanceId: string, token: string | undefined): Promise<DocumentationDraft> {
    const response = await checked(await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/cards/${encodeURIComponent(instanceId)}/submit`, {
        method: "POST", headers: await headers(token), body: JSON.stringify({ expected_revision: draft.revision, confirmation: true })
    }));
    return response.json();
}

export type DocumentationRevisionChanges = Record<string, Omit<DocumentationCardChanges, "action">>;

export async function submitDocumentationRevision(draft: DocumentationDraft, cards: DocumentationRevisionChanges, token: string | undefined): Promise<DocumentationDraft> {
    const response = await checked(await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/submit-revision`, {
        method: "POST", headers: await headers(token), body: JSON.stringify({ expected_revision: draft.revision, confirmation: true, cards })
    }));
    return response.json();
}

export async function openDocumentationNoteEdit(
    projectId: string,
    noteId: string,
    sourceDraftId: string,
    expectedNoteVersion: number,
    token: string | undefined,
    idempotencyKey: string
): Promise<DocumentationDraft> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(projectId)}/notes/${encodeURIComponent(noteId)}/documentation-edit`, {
            method: "POST",
            headers: await headers(token, idempotencyKey),
            body: JSON.stringify({
                source_draft_id: sourceDraftId,
                expected_note_version: expectedNoteVersion
            })
        })
    );
    return response.json();
}

async function processResponse(response: Response, onActivity?: () => void): Promise<DocumentationProgressEvent[]> {
    await checked(response);
    if (!response.body) throw new DocumentationApiError("EMPTY_PROCESS_RESPONSE", "The server returned no processing stream.", 502);
    return readProcessingEvents(response.body, onActivity);
}

export async function processDocumentationDraft(
    draft: DocumentationDraft,
    token: string | undefined,
    followUp?: string,
    retryInstanceId?: string,
    signal?: AbortSignal,
    onActivity?: () => void
): Promise<DocumentationProgressEvent[]> {
    // Processing bumps the revision even when it fails, so keying on the revision
    // keeps a retry idempotent without letting a second, different revision
    // request replay the first one's result.
    const key = retryInstanceId
        ? `retry-card-${draft.id}-${retryInstanceId}-${draft.revision}`
        : followUp
          ? `revise-${draft.id}-${draft.revision}`
          : `process-${draft.id}-${draft.revision}`;
    return processResponse(
        await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/process`, {
            method: "POST",
            headers: await headers(token, key),
            body: JSON.stringify({ expected_revision: draft.revision, follow_up: followUp, retry_instance_id: retryInstanceId }),
            signal
        }),
        onActivity
    );
}

export async function updateDocumentationFields(
    draft: DocumentationDraft,
    changes: Record<string, unknown>,
    confirmations: string[],
    token: string | undefined
): Promise<DocumentationDraft> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/fields`, {
            method: "PATCH",
            headers: await headers(token),
            body: JSON.stringify({ expected_revision: draft.revision, changes, confirmations })
        })
    );
    return response.json();
}

export async function updateDocumentationWorkflowNodes(
    draft: DocumentationDraft,
    changes: DocumentationWorkflowNodeChanges,
    confirmations: string[],
    token: string | undefined,
    selectedValues?: WorkflowSelectedValues
): Promise<DocumentationDraft> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/workflow-nodes`, {
            method: "PATCH",
            headers: await headers(token),
            body: JSON.stringify({ expected_revision: draft.revision, changes, confirmations, selected_values: selectedValues })
        })
    );
    return response.json();
}

export type DocumentationCardChanges = {
    action: "save" | "remove" | "retry";
    changes: DocumentationWorkflowNodeChanges;
    confirmations: string[];
    selected_values?: WorkflowSelectedValues;
    field_changes: Record<string, unknown>;
    field_confirmations: string[];
};

export async function saveDocumentationCard(
    draft: DocumentationDraft,
    instanceId: string,
    changes: DocumentationCardChanges,
    token: string | undefined
): Promise<DocumentationDraft> {
    const response = await checked(
        await fetch(
            `${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/cards/${encodeURIComponent(instanceId)}`,
            { method: "POST", headers: await headers(token), body: JSON.stringify({ ...changes, expected_revision: draft.revision }) }
        )
    );
    return response.json();
}

export async function replaceDocumentationWorkflow(draft: DocumentationDraft, workflowId: string, token: string | undefined): Promise<DocumentationDraft> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/workflow`, {
            method: "POST",
            headers: await headers(token, `replace-workflow-${draft.id}-${draft.revision}-${workflowId}`),
            body: JSON.stringify({
                expected_revision: draft.revision,
                workflow_id: workflowId,
                confirmation: true
            })
        })
    );
    return response.json();
}

/** Save step: creates the unsigned formal note from the draft. Nothing is signed here. */
export async function approveDocumentationDraft(
    draft: DocumentationDraft,
    token: string | undefined,
    idempotencyKey: string
): Promise<{ draft_id: string; status: string; note_id: string | null; signed_at?: number | null }> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/approve`, {
            method: "POST",
            headers: await headers(token, idempotencyKey),
            body: JSON.stringify({ expected_revision: draft.revision, confirmation: true })
        })
    );
    return response.json();
}

/** Sign step: signs the note the save step created, making it immutable. */
export async function signDocumentationDraft(
    draft: DocumentationDraft,
    token: string | undefined,
    idempotencyKey: string
): Promise<{ draft_id: string; status: string; note_id: string | null; signed_at?: number | null }> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/sign`, {
            method: "POST",
            headers: await headers(token, idempotencyKey),
            body: JSON.stringify({ expected_revision: draft.revision, confirmation: true })
        })
    );
    return response.json();
}

export async function cancelDocumentationDraft(draft: DocumentationDraft, token: string | undefined): Promise<DocumentationDraft> {
    const response = await checked(
        await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/cancel`, {
            method: "POST",
            headers: await headers(token),
            body: JSON.stringify({ expected_revision: draft.revision })
        })
    );
    return response.json();
}

/** Regenerate one unpublished, unsubmitted card without adding a content version. */
export async function replaceDocumentationCardWorkflow(
    draft: DocumentationDraft, instanceId: string, workflowId: string, token: string | undefined
): Promise<DocumentationDraft> {
    const response = await checked(await fetch(
        `${BACKEND_URI}/projects/${encodeURIComponent(draft.project_id)}/documentation-drafts/${encodeURIComponent(draft.id)}/cards/${encodeURIComponent(instanceId)}/workflow`,
        {
            method: "POST",
            headers: await headers(token, `replace-card-workflow-${draft.id}-${instanceId}-${draft.revision}-${workflowId}`),
            body: JSON.stringify({ expected_revision: draft.revision, workflow_id: workflowId, confirmation: true })
        }
    ));
    return response.json();
}
